import { useState, useEffect, useCallback } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Server, Save, Send, Info, CheckCircle2, XCircle, Loader2, Lock } from "lucide-react";
import ActionRequiredBanner from "./ActionRequiredBanner.jsx";

const SECURITY_OPTIONS = [
  { id: "ssl", label: "SSL (port 465)" },
  { id: "starttls", label: "STARTTLS (port 587)" },
  { id: "plain", label: "Plain (port 25)" },
];

export default function SmtpSection() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [pwInput, setPwInput] = useState(""); // password en clair, masque par defaut

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/smtp");
      setCfg(r.data);
      setPwInput(""); // toujours vide a l'init (le password n'est jamais retourne en clair)
    } catch {
      toast.error("Impossible de charger la configuration SMTP");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setCfg((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (saving) return;
    if (!cfg?.server || !cfg?.user || !cfg?.port) {
      toast.error("Serveur, port et utilisateur sont requis");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        server: cfg.server,
        port: parseInt(cfg.port, 10),
        user: cfg.user,
        security: cfg.security || "ssl",
      };
      // N'envoie le password QUE si l'utilisateur l'a saisi (sinon on conserve l'existant)
      if (pwInput && pwInput.trim() !== "") {
        payload.password = pwInput.trim();
      }
      const r = await api.put("/settings/smtp", payload);
      setCfg(r.data);
      setPwInput("");
      toast.success("Configuration SMTP enregistree");
      window.dispatchEvent(new CustomEvent("settings:updated", { detail: ["smtp"] }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (testing) return;
    if (!testTo || !/^\S+@\S+\.\S+$/.test(testTo)) {
      toast.error("Adresse email destinataire invalide");
      return;
    }
    setTesting(true);
    try {
      const r = await api.post("/settings/smtp/test", { to: testTo });
      if (r.data?.success) {
        toast.success(`Email de test envoye a ${testTo}`);
      } else {
        toast.error(r.data?.message || "Echec du test SMTP");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur reseau");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border rounded-xl p-5 text-center text-gray-400 text-sm" data-testid="smtp-section-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Chargement...
      </div>
    );
  }

  if (!cfg) return null;

  const sourceBadge = cfg.source === "db" ? (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      Persiste en DB
    </span>
  ) : (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-gray-200">
      Variables d'environnement
    </span>
  );

  const hasPasswordBadge = cfg.has_password ? (
    <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1">
      <CheckCircle2 className="w-3 h-3" /> Configure
    </span>
  ) : (
    <span className="text-[10px] text-red-600 inline-flex items-center gap-1">
      <XCircle className="w-3 h-3" /> Manquant
    </span>
  );

  return (
    <div className="space-y-4" data-testid="smtp-section">
      {/* Batch #14.52 — Bannière action requise pour SMTP */}
      <ActionRequiredBanner
        sectionKey="smtp"
        title="Action requise : configurez votre serveur SMTP"
      />
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Server className="w-4 h-4" /> Configuration du serveur SMTP
          </h3>
          {sourceBadge}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-1">Serveur SMTP</label>
            <input
              type="text"
              value={cfg.server || ""}
              onChange={(e) => update("server", e.target.value)}
              placeholder="mail.exemple.ca"
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              data-testid="smtp-server-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Port</label>
            <input
              type="number"
              min="1"
              max="65535"
              value={cfg.port || ""}
              onChange={(e) => update("port", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              data-testid="smtp-port-input"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">465 (SSL) · 587 (STARTTLS) · 25 (plain)</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Securite</label>
            <select
              value={cfg.security || "ssl"}
              onChange={(e) => update("security", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="smtp-security-select"
            >
              {SECURITY_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-1">Utilisateur (compte SMTP)</label>
            <input
              type="text"
              value={cfg.user || ""}
              onChange={(e) => update("user", e.target.value)}
              placeholder="noreply@exemple.ca"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              autoComplete="username"
              data-testid="smtp-user-input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-1 flex items-center gap-2">
              <Lock className="w-3 h-3" /> Mot de passe
              <span className="ml-auto">{hasPasswordBadge}</span>
            </label>
            <input
              type="password"
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              placeholder={cfg.has_password ? `Actuel : ${cfg.password_masked} — laisser vide pour conserver` : "Saisir le mot de passe"}
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              autoComplete="new-password"
              data-testid="smtp-password-input"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">
              Le mot de passe est chiffre en base via Fernet et n'est jamais renvoye en clair.
            </p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          data-testid="smtp-save-btn"
          aria-busy={saving}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Sauvegarde..." : "Sauvegarder"}
        </button>
      </div>

      {/* Test SMTP */}
      <div className="bg-white border rounded-xl p-5 space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Send className="w-4 h-4" /> Tester l'envoi
        </h3>
        <p className="text-xs text-gray-500">
          Envoie un vrai email de test avec la configuration ci-dessus. Le resultat
          de l'envoi (succes/echec + message d'erreur SMTP) s'affiche apres le clic.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="destinataire@exemple.ca"
            className="flex-1 border rounded-lg px-3 py-2 text-sm"
            data-testid="smtp-test-to-input"
          />
          <button
            onClick={handleTest}
            disabled={testing || !testTo}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 justify-center"
            data-testid="smtp-test-btn"
            aria-busy={testing}
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {testing ? "Envoi..." : "Envoyer le test"}
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Cette configuration prend le pas sur les variables d'environnement.
          Pour revenir aux valeurs ENV, il suffit de vider serveur/utilisateur
          et de sauvegarder (les champs vides seront ignores).
        </span>
      </div>
    </div>
  );
}
